import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Loader2, MessageSquare, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { logger } from '../utils/logger';
import { API } from '../utils/apiBase';
import { useFeatureGate } from '../utils/useFeatureGate';


const categoryColors = {
  behavioral: { bg: 'bg-[#002fa7]/10', text: 'text-[#002fa7]' },
  technical: { bg: 'bg-[#10b981]/10', text: 'text-[#10b981]' },
  situational: { bg: 'bg-[#eab308]/10', text: 'text-[#92400e]' },
};

const QuestionCard = ({ q, index }) => {
  const [expanded, setExpanded] = useState(false);
  const colors = categoryColors[q.category] || categoryColors.behavioral;

  return (
    <div className="dashboard-card" data-testid={`question-card-${index}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-6 text-left flex items-start gap-4"
        data-testid={`question-toggle-${index}`}
      >
        <span className="flex-shrink-0 w-8 h-8 bg-[#002fa7] text-white flex items-center justify-center text-sm font-bold">
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold ${colors.bg} ${colors.text} mb-2`}>
            {q.category}
          </span>
          <p className="text-[#0f1115] font-semibold text-sm leading-relaxed">{q.question}</p>
        </div>
        <span className="flex-shrink-0 text-[#6b7280] mt-1">
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>
      {expanded && (
        <div className="px-6 pb-6 border-t border-[#e5e7eb] pt-4 space-y-4">
          {q.why_asked && (
            <div>
              <p className="text-xs uppercase tracking-wider font-bold text-[#6b7280] mb-1">Why interviewers ask this</p>
              <p className="text-sm text-[#4b5563]">{q.why_asked}</p>
            </div>
          )}
          <div>
            <p className="text-xs uppercase tracking-wider font-bold text-[#10b981] mb-1">Suggested Answer</p>
            <p className="text-sm text-[#0f1115] whitespace-pre-wrap leading-relaxed bg-[#f4f5f7] p-4">{q.suggested_answer}</p>
          </div>
          {q.tips && (
            <div className="flex items-start gap-2">
              <Lightbulb size={14} className="text-[#eab308] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#4b5563]">{q.tips}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function InterviewPrep() {
  const navigate = useNavigate();
  const apiRef = useRef(API);
  const { checkGate, gateModal } = useFeatureGate();
  const [resumes, setResumes] = useState([]);
  const [selectedResume, setSelectedResume] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [questionCount, setQuestionCount] = useState(3);
  const [loading, setLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchResumes = async () => {
      try {
        const res = await axios.get(`${apiRef.current}/resumes`, { withCredentials: true });
        setResumes(res.data);
        if (res.data.length > 0) setSelectedResume(res.data[0].resume_id);
      } catch (err) {
        logger.error('Failed to fetch resumes:', err);
      }
    };
    fetchResumes();
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!selectedResume) return;
    if (await checkGate('interview_prep')) return;
    setLoading(true);
    setError('');
    setResult(null);
    setElapsedTime(0);
    timerRef.current = setInterval(() => setElapsedTime(t => t + 1), 1000);
    try {
      const res = await axios.post(`${apiRef.current}/interview-prep/generate`, {
        resume_id: selectedResume,
        job_description: jobDescription || undefined,
        question_count: questionCount,
      }, { withCredentials: true });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate interview prep.');
      logger.error('Interview prep failed:', err);
    } finally {
      clearInterval(timerRef.current);
      setLoading(false);
    }
  }, [selectedResume, jobDescription, questionCount, checkGate]);

  const behavioral = result?.questions?.filter(q => q.category === 'behavioral') || [];
  const technical = result?.questions?.filter(q => q.category === 'technical') || [];
  const situational = result?.questions?.filter(q => q.category === 'situational') || [];

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20" data-testid="interview-prep-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/dashboard')} className="inline-flex items-center space-x-2 text-[#4b5563] hover:text-[#002fa7] mb-4" data-testid="back-button">
          <ArrowLeft size={20} /><span>Back to Dashboard</span>
        </button>
        <h1 className="font-satoshi text-3xl font-black tracking-tight text-[#0f1115] mb-1">AI Interview Prep</h1>
        <p className="text-[#4b5563] mb-8">Practice with AI-generated questions tailored to your resume</p>

        {/* Input Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="dashboard-card p-6">
            <label className="block text-sm font-semibold text-[#0f1115] mb-2">
              <FileText size={16} className="inline mr-1 text-[#002fa7]" /> Select Resume *
            </label>
            <select
              value={selectedResume}
              onChange={(e) => setSelectedResume(e.target.value)}
              className="w-full px-4 py-3 border border-[#e5e7eb] bg-white text-sm focus:border-[#002fa7] transition-colors"
              data-testid="resume-select"
            >
              {resumes.map((r) => (
                <option key={r.resume_id} value={r.resume_id}>{r.title}</option>
              ))}
            </select>
          </div>

          <div className="dashboard-card p-6">
            <label className="block text-sm font-semibold text-[#0f1115] mb-2">
              <MessageSquare size={16} className="inline mr-1 text-[#002fa7]" /> Questions
            </label>
            <select
              value={questionCount}
              onChange={(e) => setQuestionCount(Number(e.target.value))}
              className="w-full px-4 py-3 border border-[#e5e7eb] bg-white text-sm focus:border-[#002fa7] transition-colors"
              data-testid="question-count-select"
            >
              {[3, 5, 8, 10, 12].map(n => (
                <option key={n} value={n}>{n} questions</option>
              ))}
            </select>
          </div>

          <div className="dashboard-card p-6 flex flex-col justify-end">
            <button
              onClick={handleGenerate}
              disabled={loading || !selectedResume}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="generate-btn"
            >
              {loading ? <><Loader2 size={18} className="animate-spin" /> Generating... {elapsedTime}s</> : 'Generate Questions'}
            </button>
            <p className="text-[10px] text-[#9ca3af] text-center mt-2">
              Generation may take up to a minute for longer resumes
            </p>
          </div>
        </div>

        <div className="dashboard-card p-6 mb-6">
          <label className="block text-sm font-semibold text-[#0f1115] mb-2">
            Job Description (optional — makes questions more specific)
          </label>
          <textarea
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            rows={4}
            placeholder="Paste the job description for more targeted questions..."
            className="w-full px-4 py-3 border border-[#e5e7eb] text-sm focus:border-[#002fa7] transition-colors resize-none"
            data-testid="jd-input"
          />
        </div>

        {error && <p className="text-sm text-[#e11d48] mb-4" data-testid="error-msg">{error}</p>}

        {/* Results */}
        {result && (
          <div className="space-y-6" data-testid="results-section">
            <div className="flex items-center justify-between text-sm text-[#6b7280]">
              <div className="flex items-center gap-4">
                <span>Prepared for: <strong className="text-[#0f1115]">{result.target_job || 'General'}</strong></span>
                <span>Resume: <strong className="text-[#0f1115]">{result.resume_title}</strong></span>
                <span>{result.questions?.length || 0} questions</span>
              </div>
              <span className="text-xs text-[#002fa7] flex items-center gap-1"><ChevronDown size={14} /> Click any question to reveal the suggested answer</span>
            </div>

            {behavioral.length > 0 && (
              <div>
                <h2 className="text-xs uppercase tracking-wider font-bold text-[#002fa7] mb-3">Behavioral Questions</h2>
                <div className="space-y-3">
                  {behavioral.map((q, i) => <QuestionCard key={`b-${i}`} q={q} index={i} />)}
                </div>
              </div>
            )}

            {technical.length > 0 && (
              <div>
                <h2 className="text-xs uppercase tracking-wider font-bold text-[#10b981] mb-3">Technical Questions</h2>
                <div className="space-y-3">
                  {technical.map((q, i) => <QuestionCard key={`t-${i}`} q={q} index={behavioral.length + i} />)}
                </div>
              </div>
            )}

            {situational.length > 0 && (
              <div>
                <h2 className="text-xs uppercase tracking-wider font-bold text-[#92400e] mb-3">Situational Questions</h2>
                <div className="space-y-3">
                  {situational.map((q, i) => <QuestionCard key={`s-${i}`} q={q} index={behavioral.length + technical.length + i} />)}
                </div>
              </div>
            )}
          </div>
        )}

        {!result && !loading && (
          <div className="dashboard-card p-12 text-center text-[#6b7280]">
            <MessageSquare size={48} className="mx-auto mb-4 text-[#d1d5db]" />
            <p className="font-semibold">Your interview questions will appear here</p>
            <p className="text-sm mt-1">Select a resume and click generate to get started</p>
          </div>
        )}
      </div>
      {gateModal}
    </div>
  );
}
